'use client';

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Mail } from 'lucide-react';
import { requestPasswordReset } from '@/lib/auth/client';
import { describeAuthInfraFailure, type AuthErrorDescription } from '@/lib/auth/auth-error';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const Schema = z.object({
  email: z.string().min(1, 'Email is required').email('Invalid email'),
});
type FormValues = z.infer<typeof Schema>;

const SUCCESS_MESSAGE = 'Check your inbox for a password reset link.';

export interface ForgotPasswordFormProps {
  /**
   * Origin the auth server trusts, supplied by the server component from the
   * runtime `BETTER_AUTH_URL` — see `lib/auth/canonical-origin.ts`.
   */
  canonicalOrigin?: string;
}

export function ForgotPasswordForm({
  canonicalOrigin = process.env.NEXT_PUBLIC_BETTER_AUTH_URL ?? '',
}: ForgotPasswordFormProps = {}) {
  const [submitted, setSubmitted] = useState(false);
  const [infraError, setInfraError] = useState<AuthErrorDescription | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(Schema),
  });

  const onSubmit = handleSubmit(async (values) => {
    setInfraError(null);
    // Account-existence outcomes are always swallowed — a real and a fake
    // address must be answered identically or this endpoint becomes an account
    // enumeration oracle. Infrastructure failures are NOT such an outcome: a
    // rejected origin, a rate limit or a 500 happen regardless of the address,
    // so telling the user costs nothing and stops us promising an email that
    // was never sent (staging 2026-08-18 — the reset 403'd on the apex host and
    // this form still said "check your inbox").
    const ctx = {
      currentOrigin: window.location.origin,
      canonicalOrigin,
      currentPath: window.location.pathname + window.location.search,
    };
    try {
      const result = await requestPasswordReset({
        email: values.email,
        redirectTo: `${window.location.origin}/reset-password`,
      });
      // The client resolves with `{ error }` rather than throwing, so the old
      // try/catch never saw an HTTP failure at all.
      const infra = result?.error
        ? describeAuthInfraFailure(result.error, ctx, { enumerationSensitive: true })
        : null;
      if (infra) {
        setInfraError(infra);
        return;
      }
    } catch {
      // A thrown error is a transport fault (offline, DNS, TLS) — never an
      // account-existence signal, so it is safe and useful to surface.
      setInfraError({
        message: "Couldn't reach the server. Check your connection and try again.",
      });
      return;
    }
    setSubmitted(true);
  });

  if (submitted) {
    return (
      <p
        role="status"
        className="rounded-lg border bg-card p-6 text-center text-sm text-fg-muted"
      >
        {SUCCESS_MESSAGE}
      </p>
    );
  }

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div className="space-y-1.5">
        <Label htmlFor="email">Work email</Label>
        <div className="relative">
          <Mail
            size={15}
            aria-hidden
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-fg-subtle"
          />
          <Input
            id="email"
            type="email"
            autoComplete="username"
            placeholder="you@company.com"
            className="pl-9"
            {...register('email')}
          />
        </div>
        {errors.email && <p className="text-sm text-destructive">{errors.email.message}</p>}
      </div>

      {infraError && (
        <p role="alert" className="text-sm text-destructive">
          {infraError.message}
          {infraError.canonicalUrl && (
            <>
              {' '}
              <a href={infraError.canonicalUrl} className="underline">
                Go to the correct address
              </a>
            </>
          )}
        </p>
      )}
      <Button type="submit" size="lg" className="w-full" disabled={isSubmitting}>
        {isSubmitting ? 'Sending…' : 'Send reset email'}
      </Button>
    </form>
  );
}
